import EditorialSection from 'components/EditorialSection';
import Section from 'components/Section';
import { usePrefersReducedMotion } from 'hooks';
import { forwardRef, lazy, Suspense, useState } from 'react';
import './index.css';

/* Heavy globe (three + topojson + world-atlas) is code-split into its own chunk.
   Only the lightweight shell and accessible places list live in the main bundle. */
const CitiesGlobe = lazy(() => import('./CitiesGlobe'));

/* ── Locations the journey passes through (plain display data) ───────────── */
/* The globe module owns the lat/lon → 3D geometry; the list only needs the
   display data. `year` orders the journey; `accent` keys the colour. */

const LOCATIONS = [
  {
    id: 'montreal',
    name: 'Montreal',
    country: 'Canada',
    year: 2019,
    period: '2019 – 2020',
    role: 'Exchange · Concordia University',
    accent: 'muted',
  },
  {
    id: 'paris',
    name: 'Paris',
    country: 'France',
    year: 2020,
    period: '2020 – Present',
    role: 'Freelance Full-Stack Engineer',
    accent: 'emerald',
    current: true,
  },
  {
    id: 'barcelona',
    name: 'Barcelona',
    country: 'Spain',
    year: 2021,
    period: '2021 – 2022',
    role: 'Exchange · Universitat Politècnica de Catalunya',
    accent: 'muted',
  },
];

const Cities = forwardRef(({ id, sectionRef }, ref) => {
  const [activeLocation, setActiveLocation] = useState(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  const activate = locId => setActiveLocation(locId);
  const deactivate = () => setActiveLocation(null);

  return (
    <Section
      as="section"
      className="cities"
      id={id}
      ref={sectionRef || ref}
      aria-labelledby={`${id}-title`}
      tabIndex={-1}
    >
      <EditorialSection
        index={5}
        eyebrow="Cities"
        title="Where I've been"
        headingId={`${id}-title`}
        meta="3 PLACES · 3 COUNTRIES"
      />

      {/* ── Two-column layout: globe (left) · places list (right) ── */}
      <div className="cities__layout">
        {/* Left column — the journey globe, floating on a soft pool of light */}
        <div className="cities__globe-col">
          <div className="cities__globe-frame">
            <Suspense
              fallback={
                <div className="cities__map-skeleton" aria-hidden="true" />
              }
            >
              <CitiesGlobe
                activeLocation={activeLocation}
                onActivate={activate}
                onDeactivate={deactivate}
                prefersReducedMotion={prefersReducedMotion}
              />
            </Suspense>
          </div>

          {/* Discreet legend (HTML, outside WebGL) — sits under the globe */}
          <div className="cities__legend">
            <span className="cities__legend-item">
              <span className="cities__legend-dot cities__legend-dot--work" />
              Work
            </span>
            <span className="cities__legend-item">
              <span className="cities__legend-dot cities__legend-dot--study" />
              Studies
            </span>
          </div>
        </div>

        {/* Right column — places list, the accessible representation */}
        <ul className="cities__places">
          {LOCATIONS.map(loc => (
            <PlaceCard
              key={loc.id}
              loc={loc}
              isActive={activeLocation === loc.id}
              onActivate={activate}
              onDeactivate={deactivate}
            />
          ))}
        </ul>
      </div>
    </Section>
  );
});

/* ── Place card ───────────────────────────────────────────────────────────── */

const PlaceCard = ({ loc, isActive, onActivate, onDeactivate }) => {
  const className = [
    'cities__place',
    `cities__place--${loc.accent}`,
    isActive ? 'cities__place--active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li
      className={className}
      tabIndex={0}
      onMouseEnter={() => onActivate(loc.id)}
      onMouseLeave={onDeactivate}
      onFocus={() => onActivate(loc.id)}
      onBlur={onDeactivate}
    >
      <div className="cities__place-head">
        <h3 className={`cities__place-name cities__place-name--${loc.accent}`}>
          {loc.name}
        </h3>
        {loc.country && (
          <span className="cities__place-country">{loc.country}</span>
        )}
      </div>

      <p className="cities__place-period cities__place-period--lead">
        {loc.period}
      </p>

      <p className="cities__place-role">{loc.role}</p>
    </li>
  );
};

export default Cities;
